import { create } from "zustand";

export interface Toast {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
  createdAt: number;
}

interface NotificationState {
  unreadCount: number;
  toasts: Toast[];
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  clearUnread: () => void;
  addToast: (toast: Omit<Toast, "id" | "createdAt">) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

let toastCounter = 0;

export const useNotificationStore = create<NotificationState>()((set) => ({
  unreadCount: 0,
  toasts: [],

  setUnreadCount: (count) => set({ unreadCount: count }),
  incrementUnread: () => set((s) => ({ unreadCount: s.unreadCount + 1 })),
  clearUnread: () => set({ unreadCount: 0 }),

  addToast: (toastData) => {
    const id = `toast-${++toastCounter}-${Date.now()}`;
    set((s) => ({
      toasts: [
        ...s.toasts,
        { ...toastData, id, createdAt: Date.now(), duration: toastData.duration ?? 4000 },
      ],
    }));
    return id;
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clearAllToasts: () => set({ toasts: [] }),
}));

// Convenience helpers (replaces next useToast hook)
export const toast = {
  success: (title: string, message?: string) =>
    useNotificationStore.getState().addToast({ type: "success", title, message }),
  error: (title: string, message?: string) =>
    useNotificationStore.getState().addToast({ type: "error", title, message, duration: 6000 }),
  warning: (title: string, message?: string) =>
    useNotificationStore.getState().addToast({ type: "warning", title, message }),
  info: (title: string, message?: string) =>
    useNotificationStore.getState().addToast({ type: "info", title, message }),
};
